import { Modal } from "flowbite-react";
import { HiOutlineUserGroup } from "react-icons/hi";

import { useFeatureFlag, FEATURE_FLAGS } from "../../contexts";
import { useGoals } from "../../hooks";
import { getCurrencySymbol } from "../../utils/currencyUtils";
import { RenewIcon, TrendUpIcon, PilotsIcon } from "./icons";
import { PilotsNotMappedCard } from "./PilotsNotMappedCard";
import { useGoalAmountFormat } from "./useGoalAmountFormat";
import { FrozenBaseLine, frozenBaseInfo } from "./FrozenBaseLine";
import {
  DASHBOARD_GOAL_TYPES,
  computeGoalTargets,
  getDashboardGoals,
  getCount,
  getFrozenBase,
  getFrozenPct,
  getPilotPool,
  getTargetPct,
  getMrrAmount,
  getQuarterLabel,
  getSummaryCurrency,
  isPilotsConversionOn,
} from "./constants";

// Three-bucket additive (PAN-520). Attrition retired — the 20% loss goal is already
// implicit in an 80% renewal goal, so a separate row would double-count Renewal.
export const GoalBreakdownModal = ({
  open,
  onClose,
  summary,
  nrrProgress,
  revenueUnit = "MRR",
}) => {
  const { goals } = useGoals();
  // PAN-587: with the flag on, the expansion base is upsells in play PLUS
  // upsells already won this quarter (the converted part), plus renewal
  // increases — not the account book — name it accordingly. Flag off keeps the
  // account-book base, so the old copy is still accurate there.
  const estimatedPillEnabled = useFeatureFlag(
    FEATURE_FLAGS.ESTIMATED_GOAL_PILL,
  );
  const fmtMrr = useGoalAmountFormat();
  // ARR is a display-only ×12 on the stored MRR amounts (PAN-523), applied here at
  // render; the targets and pools below stay MRR.
  const fmt = (value) =>
    fmtMrr(revenueUnit === "ARR" && value != null ? Number(value) * 12 : value);
  // PAN-639: a frozen goal says which base it was set against, and the current one.
  const renewalFrozenEnabled =
    useFeatureFlag(FEATURE_FLAGS.RENEWAL_GOAL_FROZEN) && estimatedPillEnabled;
  // Renewal only: the breakdown shows the dashboard's (possibly owner-scoped)
  // response, where a CSM's Expansion/Pilots goal is a re-derive on today's
  // book, not a set-time snapshot. Renewal rows are frozen per CSM (PAN-639).
  // Only when the base has moved since: an unchanged base reads the same as
  // "this quarter", and naming the set date would imply a difference.
  const provenance = (bucket, label) =>
    renewalFrozenEnabled &&
    bucket === "renewal" &&
    frozenBaseInfo(nrrProgress, bucket)?.live != null ? (
      <FrozenBaseLine
        nrrProgress={nrrProgress}
        bucket={bucket}
        label={label}
        sym={sym}
        fmt={fmt}
      />
    ) : null;

  const goalsByType = getDashboardGoals(goals);

  const retPct = getTargetPct(goalsByType, DASHBOARD_GOAL_TYPES.retention);
  const expPct = getTargetPct(goalsByType, DASHBOARD_GOAL_TYPES.expansion);
  const pilotPct = getTargetPct(goalsByType, DASHBOARD_GOAL_TYPES.pilot);

  const pcts = { retention: retPct, expansion: expPct, pilot: pilotPct };
  const {
    target: total,
    renewalTarget,
    expansionTarget,
    pilotTarget,
    pilotsMapped,
  } = computeGoalTargets(summary, nrrProgress, pcts);

  const renewalMrr = getMrrAmount(summary, "renewal");
  const expansionMrr = getMrrAmount(summary, "expansion");

  const renewalCount = getCount(summary, "renewal");
  const expansionCount = getCount(summary, "expansion");

  const pilotsPool = getPilotPool(nrrProgress);

  // Same currency rule as the widget card and the goals modal (PAN-638).
  const currency = estimatedPillEnabled
    ? (nrrProgress?.currency ?? getSummaryCurrency(summary))
    : getSummaryCurrency(summary);
  const sym = getCurrencySymbol(currency);

  // PAN-587: prefer the resolver's authoritative base + pct when the API has
  // materialised a goal. The legacy summary bucket / current slider pair is a
  // different cohort/rate from what the resolver actually used, so composing
  // ``summaryBase × sliderPct`` and asserting it equals the resolver's target
  // is a formula that only balances by accident.
  const renewalFrozenBase = getFrozenBase("renewal", nrrProgress);
  const renewalFrozenPct = getFrozenPct("renewal", nrrProgress);
  const expansionFrozenBase = getFrozenBase("expansion", nrrProgress);
  const expansionFrozenPct = getFrozenPct("expansion", nrrProgress);
  const pilotsFrozenBase = getFrozenBase("pilots", nrrProgress);
  const pilotsFrozenPct = getFrozenPct("pilots", nrrProgress);

  const renewalUsesFrozen = renewalFrozenBase !== null;
  const expansionUsesFrozen = expansionFrozenBase !== null;
  const pilotsUsesFrozen = pilotsFrozenBase !== null;
  // PAN-796: with the pilots cohort on (the backend sends pilots.live_base_mrr),
  // the base is every pilot ending this quarter — converted and lost ones too —
  // so "in play" would undersell it.
  const pilotsCohortBase = isPilotsConversionOn(nrrProgress);
  // With the conversion goal on but no pilots goal: the quarter's rate comes
  // from pilots.pct (as in the goals modal), and the base is the live cohort.
  const pilotsNoGoal = pilotsCohortBase && !pilotsUsesFrozen;
  const pilotsLiveBase = pilotsNoGoal
    ? Number(nrrProgress.pilots.live_base_mrr) || 0
    : null;
  const pilotsNoneEnding = pilotsNoGoal && pilotsLiveBase === 0;
  const pilotsBaseLabel = pilotsCohortBase
    ? `Pilot ${revenueUnit} ending this quarter`
    : `In-play pilot ${revenueUnit} this quarter`;

  const renewalBase = renewalUsesFrozen ? renewalFrozenBase : renewalMrr;
  const expansionBase = expansionUsesFrozen
    ? expansionFrozenBase
    : expansionMrr;
  const pilotsBase = pilotsUsesFrozen ? pilotsFrozenBase : pilotsPool;

  const renewalPctToShow = renewalUsesFrozen ? renewalFrozenPct : retPct;
  const expansionPctToShow = expansionUsesFrozen ? expansionFrozenPct : expPct;
  const pilotsPctToShow = pilotsUsesFrozen
    ? pilotsFrozenPct
    : pilotsNoGoal && nrrProgress.pilots.pct != null
      ? Number(nrrProgress.pilots.pct)
      : pilotPct;

  // pct_at_set is null when a leader typed a fixed-dollar target directly —
  // there is no rate to display, so the formula shape collapses to a plain
  // "committed" line.
  const renewalHasFormula = !renewalUsesFrozen || renewalFrozenPct !== null;
  const expansionHasFormula =
    !expansionUsesFrozen || expansionFrozenPct !== null;
  const pilotsHasFormula = !pilotsUsesFrozen || pilotsFrozenPct !== null;

  const BREAKDOWN_ITEMS = [
    {
      id: "renewals",
      bgClass: "bg-amber-50",
      borderClass: "border-amber-200",
      labelClass: "text-amber-900",
      icon: RenewIcon,
      label: "Renewals",
      value: `${sym}${fmt(renewalTarget)}`,
      body:
        provenance("renewal", "Renewal cohort") ??
        (renewalUsesFrozen ? (
          <>
            Renewal cohort this quarter:{" "}
            <strong>
              {sym}
              {fmt(renewalBase)}
            </strong>
          </>
        ) : (
          <>
            Your book:{" "}
            <strong>
              {sym}
              {fmt(renewalMrr)}
            </strong>{" "}
            across {renewalCount} accounts due this quarter
          </>
        )),
      formula: renewalHasFormula
        ? `${sym}${fmt(renewalBase)} × ${renewalPctToShow}% retention target = ${sym}${fmt(renewalTarget)}`
        : `Set by CS Lead as a fixed target: ${sym}${fmt(renewalTarget)}`,
    },
    {
      id: "expansion",
      bgClass: "bg-emerald-50",
      borderClass: "border-emerald-200",
      labelClass: "text-emerald-900",
      icon: TrendUpIcon,
      label: "Expansion",
      value: `${sym}${fmt(expansionTarget)}`,
      body:
        provenance("expansion", "Expansion in play") ??
        (expansionUsesFrozen ? (
          <>
            {estimatedPillEnabled
              ? "Expansion in play this quarter:"
              : "Expandable book this quarter:"}{" "}
            <strong>
              {sym}
              {fmt(expansionBase)}
            </strong>
          </>
        ) : (
          <>
            Your pipeline:{" "}
            <strong>
              {sym}
              {fmt(expansionMrr)}
            </strong>{" "}
            across {expansionCount} accounts with growth signals
          </>
        )),
      formula: expansionHasFormula
        ? `${sym}${fmt(expansionBase)} × ${expansionPctToShow}% conversion target = ${sym}${fmt(expansionTarget)}`
        : `Set by CS Lead as a fixed target: ${sym}${fmt(expansionTarget)}`,
    },
    ...(pilotsMapped
      ? [
          {
            id: "pilots",
            bgClass: "bg-indigo-50",
            borderClass: "border-indigo-200",
            labelClass: "text-indigo-900",
            icon: PilotsIcon,
            label: "Pilots",
            value: `${sym}${fmt(pilotTarget)}`,
            body:
              provenance("pilots", pilotsBaseLabel) ??
              (pilotsUsesFrozen || pilotsNoGoal ? (
                <>
                  {pilotsBaseLabel}:{" "}
                  <strong>
                    {sym}
                    {fmt(pilotsNoGoal ? pilotsLiveBase : pilotsBase)}
                  </strong>
                </>
              ) : (
                <>
                  Pilot pool:{" "}
                  <strong>
                    {sym}
                    {fmt(pilotsPool)}
                  </strong>{" "}
                  of {revenueUnit} in play + already converted this quarter
                </>
              )),
            formula: pilotsNoneEnding
              ? "No pilots end this quarter"
              : pilotsNoGoal
                ? `${sym}${fmt(pilotsLiveBase)} × ${pilotsPctToShow}% conversion target is under ${sym}1, so there is no pilots goal this quarter`
                : pilotsHasFormula
                  ? `${sym}${fmt(pilotsBase)} × ${pilotsPctToShow}% conversion target = ${sym}${fmt(pilotTarget)}`
                  : `Set by CS Lead as a fixed target: ${sym}${fmt(pilotTarget)}`,
          },
        ]
      : []),
  ];

  return (
    <Modal show={open} onClose={onClose} size="2xl" popup dismissible>
      <div className="rounded-2xl bg-white shadow-none">
        {/* Header */}
        <div className="flex items-start justify-between border-b border-slate-100 px-8 pb-5 pt-7">
          <div>
            <div className="text-lg font-extrabold tracking-[-0.3px] text-slate-800">
              Your {getQuarterLabel()} Goal Breakdown
            </div>
            <div className="mt-1 text-[13px] text-slate-400">
              How your personal target is calculated from your book of business
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="cursor-pointer border-none bg-transparent p-1 text-xl leading-none text-slate-400 transition-colors hover:text-slate-600"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          {/* Team targets banner */}
          <div className="mb-6 flex items-center gap-3 rounded-[10px] border border-slate-200 bg-slate-50 px-[18px] py-3.5">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-indigo-50 text-indigo-500">
              <HiOutlineUserGroup size={20} />
            </div>
            <div className="flex-1">
              <div className="text-xs font-semibold text-slate-500">
                Team targets set by CS Lead
              </div>
              <div className="mt-0.5 text-[13px] text-slate-800">
                <span className="font-bold">{retPct}%</span> retention ·{" "}
                <span className="font-bold">{expPct}%</span> expansion
                {pilotsMapped ? (
                  <>
                    {" "}
                    ·{" "}
                    <span className="font-bold">
                      {/* PAN-796: with the conversion goal on, the quarter's
                          rate (committed, else the estimate) — the goal's, or
                          pilots.pct when there is no goal; the goals modal
                          seeds from the same. Off: the saved legacy %. */}
                      {pilotsCohortBase ? pilotsPctToShow : pilotPct}%
                    </span>{" "}
                    pilot conversion
                  </>
                ) : null}
              </div>
            </div>
          </div>

          {/* Breakdown cards */}
          <div className="mb-6 flex flex-col gap-4">
            {BREAKDOWN_ITEMS.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.id}
                  className={`rounded-xl border px-5 py-[18px] ${item.bgClass} ${item.borderClass}`}
                >
                  <div className="mb-2 flex items-start justify-between">
                    <div
                      className={`flex items-center gap-2 ${item.labelClass}`}
                    >
                      <Icon />
                      <span className="text-sm font-bold">{item.label}</span>
                    </div>
                    <span className="text-xl font-extrabold tracking-[-0.5px] text-slate-800">
                      {item.value}
                    </span>
                  </div>
                  <div className="text-[13px] text-slate-500">{item.body}</div>
                  <div className="mt-1 text-xs text-slate-400">
                    {item.formula}
                  </div>
                </div>
              );
            })}
            {!pilotsMapped && <PilotsNotMappedCard variant="breakdown" />}
          </div>

          {/* Total row — additive sum, always ≥ 0 */}
          <div className="flex items-center justify-between rounded-[14px] border-2 border-slate-200 bg-slate-50 px-6 py-5">
            <span className="text-[15px] font-bold text-slate-800">
              Your {getQuarterLabel()} Goal
            </span>
            <span className="text-[28px] font-extrabold tracking-[-1px] text-indigo-600">
              {sym}
              {fmt(total)}
            </span>
          </div>

          {/* Footer */}
          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="cursor-pointer rounded-[10px] border-none bg-slate-800 px-6 py-2.5 text-[13px] font-semibold text-white transition-colors hover:bg-slate-700"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
